import React, { useState, useCallback, useEffect, useRef } from 'react';
import { Input } from 'antd';
import styled from '@emotion/styled';

const { TextArea } = Input;

const TAG_PATTERNS = [
  /\[\[.*?\] from .*?\]/g,
  /\[Input from .*?\]/g,
  /{{.*?}}/g,
];

const InputContainer = styled.div`
  position: relative;
  width: 100%;
`;

const StyledDisplay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  padding: 4px 11px;
  font-size: 14px;
  line-height: 1.5715;
  pointer-events: none;
  white-space: pre-wrap;
  word-wrap: break-word;
  overflow: hidden;
  color: transparent;
  background: transparent;
  z-index: 1;
`;

const StyledTextArea = styled(TextArea)`
  background: transparent !important;
`;

const StyledText = styled.span`
  display: inline;
  white-space: pre-wrap;
  color: transparent;
  background: ${props => {
    switch (props.type) {
      case 'input':
        return 'rgba(24, 144, 255, 0.15)';
      case 'dynamic':
        return 'rgba(250, 140, 22, 0.15)';
      default:
        return 'transparent';
    }
  }};
  border-radius: 2px;
`;

const AtomicTagInput = ({ value, onChange, placeholder, className, rows = 4, onCursorChange }) => {
  const textAreaRef = useRef(null);
  const displayRef = useRef(null);
  const [displayParts, setDisplayParts] = useState([]);

  const findTags = useCallback((text) => {
    let tags = [];
    TAG_PATTERNS.forEach((pattern, index) => {
      let match;
      pattern.lastIndex = 0;
      while ((match = pattern.exec(text)) !== null) {
        tags.push({
          start: match.index,
          end: match.index + match[0].length,
          text: match[0],
          type: index === 2 ? 'dynamic' : 'input'
        });
      }
    });
    return tags.sort((a, b) => a.start - b.start);
  }, []);

  const parseTextForDisplay = useCallback((text) => {
    const tags = findTags(text);
    const parts = [];
    let lastIndex = 0;

    tags.forEach(tag => {
      if (tag.start > lastIndex) {
        parts.push({
          type: 'text',
          text: text.slice(lastIndex, tag.start)
        });
      }
      parts.push({
        type: tag.type,
        text: tag.text
      });
      lastIndex = tag.end;
    });

    if (lastIndex < text.length) {
      parts.push({
        type: 'text',
        text: text.slice(lastIndex)
      });
    }

    return parts;
  }, [findTags]);

  // Sync scroll position
  const handleScroll = useCallback((e) => {
    if (displayRef.current) {
      displayRef.current.scrollTop = e.target.scrollTop;
    }
  }, []);

  useEffect(() => {
    setDisplayParts(parseTextForDisplay(value));
    const textArea = textAreaRef.current?.resizableTextArea?.textArea;
    if (textArea) {
      textArea.addEventListener('scroll', handleScroll);
      return () => textArea.removeEventListener('scroll', handleScroll);
    }
  }, [value, parseTextForDisplay, handleScroll]);

  const handleKeyDown = useCallback((e) => {
    const textArea = textAreaRef.current?.resizableTextArea?.textArea;
    if (!textArea) return;

    const text = e.target.value;
    const cursorPos = e.target.selectionStart;
    const tags = findTags(text);

    const activeTag = tags.find(tag => 
      cursorPos > tag.start && cursorPos <= tag.end
    );

    if (e.key === 'Backspace' && activeTag) {
      e.preventDefault();
      const newText = text.slice(0, activeTag.start) + text.slice(activeTag.end);
      onChange(newText);
      
      requestAnimationFrame(() => {
        onCursorChange(activeTag.start);
        const updatedTextArea = textAreaRef.current?.resizableTextArea?.textArea;
        if (updatedTextArea) {
          updatedTextArea.setSelectionRange(activeTag.start, activeTag.start);
        }
      });
    }
  }, [findTags, onChange, onCursorChange]);

  const handleSelect = useCallback((e) => {
    onCursorChange(e.target.selectionStart);
  }, [onCursorChange]);

  const handleChange = useCallback((e) => {
    onChange(e.target.value);
    onCursorChange(e.target.selectionStart);
  }, [onChange, onCursorChange]);

  return (
    <InputContainer>
      <StyledTextArea
        ref={textAreaRef}
        value={value}
        onChange={handleChange}
        onKeyDown={handleKeyDown}
        onSelect={handleSelect}
        onClick={handleSelect}
        placeholder={placeholder}
        className={className}
        rows={rows}
      />
      <StyledDisplay ref={displayRef}>
        {displayParts.map((part, index) => (
          <StyledText key={index} type={part.type}>
            {part.text}
          </StyledText>
        ))}
      </StyledDisplay>
    </InputContainer>
  );
};

export default AtomicTagInput;